import React, { PureComponent } from "react";
import _ from "lodash";
import autoBind from "react-autobind";
import { Alert, Button, ControlLabel, FormControl, FormGroup, Label, Tab, Tabs } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const DEFAULT_BASE_FIELD = "value";
const DEFAULT_RETAIL_TIERS = {
	default: 2.6,
	baseField: DEFAULT_BASE_FIELD,
	ranges: [
		{ min: 0, max: 1, value: 3 },
		{ min: 1000, max: 50000, value: 2 }
	]
};

const BASE_FIELD_OPTIONS = [
	{ value: "value", label: "Base Value" },
	{ value: "pricingModeTierBase", label: "Pricing Tier Base" },
	{ value: "pricingBaseValue", label: "Pricing Base Value" },
	{ value: "rate", label: "Rate" }
];

const PREVIEW_AMOUNTS = [0.5, 24, 250, 1000, 5000];

function toJson(value) {
	return JSON.stringify(value || {}, null, 4);
}

function toFiniteNumber(value, fallback = null) {
	if (value === "" || value === null || value === undefined) {
		return fallback;
	}
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeRange(range = {}) {
	return {
		min: toFiniteNumber(range.min, 0),
		max: toFiniteNumber(range.max, null),
		value: toFiniteNumber(range.value, 1)
	};
}

function sortRanges(ranges = []) {
	return [...ranges].sort((left, right) => {
		const leftMin = toFiniteNumber(left.min, Number.NEGATIVE_INFINITY);
		const rightMin = toFiniteNumber(right.min, Number.NEGATIVE_INFINITY);
		if (leftMin !== rightMin) {
			return leftMin - rightMin;
		}
		return toFiniteNumber(left.max, Number.POSITIVE_INFINITY) - toFiniteNumber(right.max, Number.POSITIVE_INFINITY);
	});
}

function normalizeTiers(value = {}) {
	const source = _.isPlainObject(value) ? value : {};
	return {
		default: toFiniteNumber(source.default, DEFAULT_RETAIL_TIERS.default),
		baseField: _.toString(source.baseField || DEFAULT_BASE_FIELD).trim() || DEFAULT_BASE_FIELD,
		ranges: sortRanges(_.isArray(source.ranges) ? source.ranges.map(normalizeRange) : [])
	};
}

function parseRetailTiersContent(content) {
	if (!content || (_.isString(content) && _.isEmpty(content))) {
		return {
			wrapperKey: "",
			document: _.cloneDeep(DEFAULT_RETAIL_TIERS),
			tiers: _.cloneDeep(DEFAULT_RETAIL_TIERS)
		};
	}

	const parsed = _.isString(content) ? JSON.parse(content) : content;
	const document = _.isPlainObject(parsed) ? _.cloneDeep(parsed) : _.cloneDeep(DEFAULT_RETAIL_TIERS);
	const wrapperKey = _.isPlainObject(document.retailTiers)
		? "retailTiers"
		: _.isPlainObject(_.get(document, "pricingModes.retail.retailTiers"))
			? "pricingModes.retail.retailTiers"
			: "";
	const tiers = wrapperKey
		? normalizeTiers(_.get(document, wrapperKey))
		: normalizeTiers(document);

	return { wrapperKey, document, tiers };
}

function buildRetailTiersDocument(document = {}, wrapperKey = "", tiers = {}) {
	const normalizedTiers = normalizeTiers(tiers);
	if (wrapperKey) {
		const nextDocument = _.cloneDeep(document || {});
		_.set(nextDocument, wrapperKey, normalizedTiers);
		return nextDocument;
	}
	return normalizedTiers;
}

function formatMoney(value) {
	const amount = toFiniteNumber(value, 0);
	return amount.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 2
	});
}

function formatMultiplier(value) {
	const amount = toFiniteNumber(value, 0);
	return `${amount.toLocaleString("en-US", { maximumFractionDigits: 4 })}x`;
}

function getTierMultiplier(tiers = {}, amount = 0) {
	const ranges = _.isArray(tiers.ranges) ? tiers.ranges : [];
	for (const range of ranges) {
		const min = toFiniteNumber(range.min, Number.NEGATIVE_INFINITY);
		const max = toFiniteNumber(range.max, Number.POSITIVE_INFINITY);
		if (amount >= min && amount <= max) {
			return toFiniteNumber(range.value, toFiniteNumber(tiers.default, 1));
		}
	}
	return toFiniteNumber(tiers.default, 1);
}

function validateTiers(tiers = {}) {
	const issues = [];
	const normalizedDefault = toFiniteNumber(tiers.default, null);
	if (normalizedDefault === null || normalizedDefault < 0) {
		issues.push("Default multiplier must be a non-negative number.");
	}

	const ranges = _.isArray(tiers.ranges) ? tiers.ranges : [];
	ranges.forEach((range, index) => {
		const min = toFiniteNumber(range.min, null);
		const max = toFiniteNumber(range.max, null);
		const value = toFiniteNumber(range.value, null);
		if (min === null) {
			issues.push(`Tier ${index + 1} requires a minimum amount.`);
		}
		if (max !== null && min !== null && max < min) {
			issues.push(`Tier ${index + 1} maximum must be greater than or equal to minimum.`);
		}
		if (value === null || value < 0) {
			issues.push(`Tier ${index + 1} multiplier must be a non-negative number.`);
		}
	});

	const sortedRanges = sortRanges(ranges);
	for (let i = 1; i < sortedRanges.length; i++) {
		const previous = sortedRanges[i - 1];
		const current = sortedRanges[i];
		const previousMax = toFiniteNumber(previous.max, Number.POSITIVE_INFINITY);
		const currentMin = toFiniteNumber(current.min, Number.NEGATIVE_INFINITY);
		if (previousMax >= currentMin) {
			issues.push(`Tier ranges overlap around ${formatMoney(currentMin)}.`);
			break;
		}
	}

	return issues;
}

export default class RetailTiersEditor extends PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);

		const parsed = this.parseContentWithFallback(props.content);
		const jsonText = toJson(buildRetailTiersDocument(parsed.document, parsed.wrapperKey, parsed.tiers));
		this.lastEmittedContent = jsonText;
		this.state = {
			activeTab: "visual",
			document: parsed.document,
			wrapperKey: parsed.wrapperKey,
			tiers: parsed.tiers,
			jsonText,
			jsonError: ""
		};
		this.emitChange(jsonText, validateTiers(parsed.tiers));
	}

	componentDidUpdate(prevProps) {
		if (prevProps.content !== this.props.content && this.props.content !== this.lastEmittedContent) {
			const parsed = this.parseContentWithFallback(this.props.content);
			const jsonText = toJson(buildRetailTiersDocument(parsed.document, parsed.wrapperKey, parsed.tiers));
			this.lastEmittedContent = jsonText;
			this.setState({
				document: parsed.document,
				wrapperKey: parsed.wrapperKey,
				tiers: parsed.tiers,
				jsonText,
				jsonError: ""
			});
			this.emitChange(jsonText, validateTiers(parsed.tiers));
		}
	}

	getContent() {
		if (this.state.jsonError) {
			return this.state.jsonText;
		}
		return toJson(buildRetailTiersDocument(this.state.document, this.state.wrapperKey, this.state.tiers));
	}

	parseContentWithFallback(content) {
		try {
			return parseRetailTiersContent(content);
		} catch (err) {
			console.warn("Unable to parse retail tiers, falling back to default tiers.", err);
			return parseRetailTiersContent(DEFAULT_RETAIL_TIERS);
		}
	}

	emitChange(content, issues = []) {
		const valid = issues.length === 0;
		this.lastEmittedContent = content;
		if (_.isFunction(this.props.onChange)) {
			this.props.onChange(content);
		}
		if (_.isFunction(this.props.onValidityChange)) {
			this.props.onValidityChange({
				valid,
				error: valid ? null : new Error(issues.join(" "))
			});
		}
	}

	applyTiers(nextTiers) {
		const tiers = normalizeTiers(nextTiers);
		const jsonText = toJson(buildRetailTiersDocument(this.state.document, this.state.wrapperKey, tiers));
		this.setState({
			tiers,
			jsonText,
			jsonError: ""
		});
		this.emitChange(jsonText, validateTiers(tiers));
	}

	handleTiersFieldChange(field, value) {
		this.applyTiers({
			...this.state.tiers,
			[field]: value
		});
	}

	handleRangeFieldChange(rangeIndex, field, value) {
		const ranges = [...(this.state.tiers.ranges || [])];
		ranges[rangeIndex] = {
			...(ranges[rangeIndex] || {}),
			[field]: value === "" && field === "max" ? null : toFiniteNumber(value, value)
		};
		this.applyTiers({
			...this.state.tiers,
			ranges
		});
	}

	handleAddRange() {
		const ranges = sortRanges(this.state.tiers.ranges || []);
		const lastRange = ranges[ranges.length - 1] || {};
		const lastMax = toFiniteNumber(lastRange.max, 0);
		const nextMin = Number.isFinite(lastMax) ? lastMax + 1 : 0;
		ranges.push({
			min: nextMin,
			max: null,
			value: this.state.tiers.default
		});
		this.applyTiers({
			...this.state.tiers,
			ranges
		});
	}

	handleRemoveRange(rangeIndex) {
		const ranges = [...(this.state.tiers.ranges || [])];
		ranges.splice(rangeIndex, 1);
		this.applyTiers({
			...this.state.tiers,
			ranges
		});
	}

	handleMoveRange(rangeIndex, direction) {
		const ranges = [...(this.state.tiers.ranges || [])];
		const nextIndex = rangeIndex + direction;
		if (nextIndex < 0 || nextIndex >= ranges.length) {
			return;
		}
		const [range] = ranges.splice(rangeIndex, 1);
		ranges.splice(nextIndex, 0, range);
		this.applyTiers({
			...this.state.tiers,
			ranges
		});
	}

	handleSortRanges() {
		this.applyTiers({
			...this.state.tiers,
			ranges: sortRanges(this.state.tiers.ranges || [])
		});
	}

	handleJsonTextChange(event) {
		const jsonText = event.target.value;
		try {
			const parsed = parseRetailTiersContent(jsonText);
			const normalizedJson = toJson(buildRetailTiersDocument(parsed.document, parsed.wrapperKey, parsed.tiers));
			const issues = validateTiers(parsed.tiers);
			this.setState({
				document: parsed.document,
				wrapperKey: parsed.wrapperKey,
				tiers: parsed.tiers,
				jsonText,
				jsonError: ""
			});
			this.emitChange(normalizedJson, issues);
		} catch (err) {
			this.setState({
				jsonText,
				jsonError: err.message
			});
			this.lastEmittedContent = jsonText;
			if (_.isFunction(this.props.onChange)) {
				this.props.onChange(jsonText);
			}
			if (_.isFunction(this.props.onValidityChange)) {
				this.props.onValidityChange({ valid: false, error: err });
			}
		}
	}

	renderNumberField(label, value, onChange, options = {}) {
		return (
			<FormGroup style={options.style || styles.formGroup}>
				<ControlLabel>{label}</ControlLabel>
				<FormControl
					type="number"
					step={options.step || "0.01"}
					min={options.min}
					value={value === null || value === undefined ? "" : value}
					placeholder={options.placeholder || ""}
					onChange={(event) => onChange(event.target.value)}
				/>
			</FormGroup>
		);
	}

	renderSummary() {
		const tiers = this.state.tiers || {};
		const issues = validateTiers(tiers);
		const ranges = tiers.ranges || [];
		return (
			<div style={styles.summaryBand}>
				<div style={styles.summaryItem}>
					<div style={styles.summaryLabel}>Default Multiplier</div>
					<div style={styles.summaryValue}>{formatMultiplier(tiers.default)}</div>
				</div>
				<div style={styles.summaryItem}>
					<div style={styles.summaryLabel}>Tier Base</div>
					<div style={styles.summaryValue}>{tiers.baseField || DEFAULT_BASE_FIELD}</div>
				</div>
				<div style={styles.summaryItem}>
					<div style={styles.summaryLabel}>Configured Ranges</div>
					<div style={styles.summaryValue}>{ranges.length}</div>
				</div>
				<div style={styles.summaryItem}>
					<div style={styles.summaryLabel}>Validation</div>
					<div style={styles.summaryValue}>
						<Label bsStyle={issues.length ? "danger" : "success"}>
							{issues.length ? `${issues.length} issue${issues.length === 1 ? "" : "s"}` : "Ready"}
						</Label>
					</div>
				</div>
			</div>
		);
	}

	renderSettings() {
		const tiers = this.state.tiers || {};
		return (
			<div style={styles.section}>
				<div style={styles.sectionHeader}>
					<div>
						<div style={styles.sectionTitle}>Retail Rule</div>
						<div style={styles.hint}>Each invoice item starts with its base price, then Retail applies the matching multiplier.</div>
					</div>
				</div>
				<div style={styles.grid}>
					{this.renderNumberField("Default Multiplier", tiers.default, value => this.handleTiersFieldChange("default", toFiniteNumber(value, 0)), {
						step: "0.01",
						min: "0"
					})}
					<FormGroup style={styles.formGroup}>
						<ControlLabel>Tier Base Field</ControlLabel>
						<FormControl
							componentClass="select"
							value={tiers.baseField || DEFAULT_BASE_FIELD}
							onChange={(event) => this.handleTiersFieldChange("baseField", event.target.value)}
						>
							{BASE_FIELD_OPTIONS.map(option => (
								<option key={option.value} value={option.value}>{option.label} ({option.value})</option>
							))}
						</FormControl>
					</FormGroup>
				</div>
			</div>
		);
	}

	renderRange(range = {}, rangeIndex) {
		const min = toFiniteNumber(range.min, 0);
		const max = toFiniteNumber(range.max, null);
		const multiplier = toFiniteNumber(range.value, this.state.tiers.default);
		const sampleAmount = max === null || !Number.isFinite(max) ? Math.max(min, 1000) : ((min + max) / 2);
		const sampleRetail = sampleAmount * multiplier;
		return (
			<div key={`retail-tier-${rangeIndex}`} style={styles.rangeRow}>
				<div style={styles.rangeIndex}>{rangeIndex + 1}</div>
				<div style={styles.rangeFields}>
					{this.renderNumberField("Min", range.min, value => this.handleRangeFieldChange(rangeIndex, "min", value), {
						step: "0.01",
						min: "0",
						style: styles.rangeFormGroup
					})}
					{this.renderNumberField("Max", range.max, value => this.handleRangeFieldChange(rangeIndex, "max", value), {
						step: "0.01",
						min: "0",
						placeholder: "No limit",
						style: styles.rangeFormGroup
					})}
					{this.renderNumberField("Multiplier", range.value, value => this.handleRangeFieldChange(rangeIndex, "value", value), {
						step: "0.01",
						min: "0",
						style: styles.rangeFormGroup
					})}
				</div>
				<div style={styles.rangePreview}>
					<div style={styles.previewLabel}>Example</div>
					<div style={styles.previewValue}>{formatMoney(sampleAmount)} -> {formatMoney(sampleRetail)}</div>
					<div style={styles.previewHint}>{formatMultiplier(multiplier)} for {formatMoney(min)} to {max === null ? "no limit" : formatMoney(max)}</div>
				</div>
				<div style={styles.rowActions}>
					<Button bsSize="xsmall" onClick={() => this.handleMoveRange(rangeIndex, -1)} disabled={rangeIndex === 0} title="Move up">
						<FontAwesomeIcon icon="arrow-up" />
					</Button>
					<Button bsSize="xsmall" onClick={() => this.handleMoveRange(rangeIndex, 1)} disabled={rangeIndex >= (this.state.tiers.ranges || []).length - 1} title="Move down">
						<FontAwesomeIcon icon="arrow-down" />
					</Button>
					<Button bsSize="xsmall" bsStyle="danger" onClick={() => this.handleRemoveRange(rangeIndex)} title="Remove tier">
						<FontAwesomeIcon icon="trash" />
					</Button>
				</div>
			</div>
		);
	}

	renderRanges() {
		const ranges = this.state.tiers.ranges || [];
		const issues = validateTiers(this.state.tiers);
		return (
			<div style={styles.section}>
				<div style={styles.sectionHeader}>
					<div>
						<div style={styles.sectionTitle}>Tier Ranges</div>
						<div style={styles.hint}>Ranges are inclusive. Items outside every range use the default multiplier.</div>
					</div>
					<div style={styles.sectionActions}>
						<Button bsSize="small" onClick={this.handleSortRanges}>
							<FontAwesomeIcon icon="sort-amount-down" /> Sort
						</Button>
						<Button bsSize="small" bsStyle="primary" onClick={this.handleAddRange}>
							<FontAwesomeIcon icon="plus" /> Add Tier
						</Button>
					</div>
				</div>
				{issues.length > 0 && (
					<Alert bsStyle="danger" style={styles.alert}>
						{issues.map(issue => <div key={issue}>{issue}</div>)}
					</Alert>
				)}
				{ranges.length === 0 ? (
					<Alert bsStyle="warning" style={styles.alert}>No tier ranges are configured. All items will use the default multiplier.</Alert>
				) : ranges.map(this.renderRange)}
			</div>
		);
	}

	renderPreview() {
		const tiers = this.state.tiers || {};
		return (
			<div style={styles.section}>
				<div style={styles.sectionTitle}>Preview</div>
				<div style={styles.previewGrid}>
					{PREVIEW_AMOUNTS.map(amount => {
						const multiplier = getTierMultiplier(tiers, amount);
						return (
							<div key={`retail-preview-${amount}`} style={styles.previewCard}>
								<div style={styles.summaryLabel}>Base</div>
								<div style={styles.previewBase}>{formatMoney(amount)}</div>
								<div style={styles.previewMath}>{formatMultiplier(multiplier)} -> {formatMoney(amount * multiplier)}</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	renderJsonEditor() {
		return (
			<div style={styles.section}>
				{this.state.jsonError && (
					<Alert bsStyle="danger">Invalid JSON: {this.state.jsonError}</Alert>
				)}
				<FormControl
					componentClass="textarea"
					value={this.state.jsonText}
					rows={26}
					onChange={this.handleJsonTextChange}
					style={styles.jsonTextArea}
				/>
			</div>
		);
	}

	render() {
		return (
			<div style={styles.container}>
				<Tabs activeKey={this.state.activeTab} onSelect={(activeTab) => this.setState({ activeTab })} id="retail-tiers-editor-tabs">
					<Tab eventKey="visual" title={<span><FontAwesomeIcon icon="sliders-h" /> Visual</span>}>
						{this.renderSummary()}
						{this.renderSettings()}
						{this.renderRanges()}
						{this.renderPreview()}
					</Tab>
					<Tab eventKey="json" title={<span><FontAwesomeIcon icon={["far", "file-code"]} /> JSON</span>}>
						{this.renderJsonEditor()}
					</Tab>
				</Tabs>
			</div>
		);
	}
}

const styles = {
	container: {
		minHeight: "620px",
		paddingBottom: "12px"
	},
	summaryBand: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
		gap: "10px",
		marginTop: "14px"
	},
	summaryItem: {
		padding: "12px",
		border: "1px solid #d9e2ec",
		background: "#f8fafc",
		borderRadius: "4px"
	},
	summaryLabel: {
		fontSize: "12px",
		color: "#627d98",
		fontWeight: 700,
		textTransform: "uppercase"
	},
	summaryValue: {
		marginTop: "4px",
		fontSize: "18px",
		fontWeight: 700,
		color: "#102a43"
	},
	section: {
		marginTop: "14px",
		padding: "12px",
		border: "1px solid #d9e2ec",
		background: "#fff",
		borderRadius: "4px"
	},
	sectionHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "12px",
		marginBottom: "10px",
		flexWrap: "wrap"
	},
	sectionTitle: {
		fontWeight: 700,
		fontSize: "16px",
		color: "#243b53"
	},
	hint: {
		fontSize: "12px",
		color: "#627d98"
	},
	sectionActions: {
		display: "flex",
		gap: "6px",
		flexWrap: "wrap"
	},
	grid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
		gap: "10px 12px",
		alignItems: "start"
	},
	formGroup: {
		marginBottom: "8px"
	},
	rangeRow: {
		display: "grid",
		gridTemplateColumns: "36px minmax(360px, 1fr) minmax(220px, 280px) auto",
		gap: "10px",
		alignItems: "center",
		padding: "10px",
		border: "1px solid #bcccdc",
		background: "#f8fafc",
		borderRadius: "4px",
		marginTop: "10px"
	},
	rangeIndex: {
		width: "28px",
		height: "28px",
		lineHeight: "28px",
		textAlign: "center",
		borderRadius: "50%",
		background: "#d9e2ec",
		color: "#102a43",
		fontWeight: 700
	},
	rangeFields: {
		display: "grid",
		gridTemplateColumns: "repeat(3, minmax(95px, 1fr))",
		gap: "8px"
	},
	rangeFormGroup: {
		marginBottom: 0
	},
	rangePreview: {
		fontSize: "12px",
		color: "#334e68",
		lineHeight: 1.35
	},
	previewLabel: {
		fontWeight: 700,
		color: "#627d98"
	},
	previewValue: {
		fontWeight: 700,
		color: "#102a43"
	},
	previewHint: {
		color: "#627d98"
	},
	rowActions: {
		display: "flex",
		gap: "4px"
	},
	alert: {
		marginBottom: "10px"
	},
	previewGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
		gap: "10px",
		marginTop: "10px"
	},
	previewCard: {
		border: "1px solid #d9e2ec",
		borderRadius: "4px",
		padding: "10px",
		background: "#f8fafc"
	},
	previewBase: {
		fontWeight: 700,
		fontSize: "16px",
		color: "#102a43",
		marginTop: "2px"
	},
	previewMath: {
		marginTop: "4px",
		fontSize: "12px",
		color: "#334e68"
	},
	jsonTextArea: {
		fontFamily: "Menlo, Consolas, monospace",
		fontSize: "12px",
		minHeight: "520px"
	}
};
